import React from 'react'
import {
  FileText,
  FileSearch,
  Brain,
  Layers3,
  FileSpreadsheet,
  Save,
  CheckCircle2,
  Check,
  type LucideIcon
} from 'lucide-react'
import AIAgentOrbLoader from '../../../AIAgentOrb/AIAgentOrbLoader.tsx'
import orbStyles from '../../../AIAgentOrb/AIAgentOrbLoader.module.scss'
import { formatDocumentationPipelineFileSize } from '../../../../../services/projectAutofillDraftService.ts'
import styles from './DocumentPipelineLoopLoader.module.scss'

const PIPELINE_STEPS: { phase: string; label: string; Icon: LucideIcon }[] = [
  { phase: 'draft_ready', label: 'Borrador listo', Icon: FileText },
  { phase: 'extracting_text', label: 'Extrayendo texto del PDF', Icon: FileSearch },
  { phase: 'analyzing_document', label: 'Analizando el documento', Icon: Brain },
  { phase: 'matching_skills', label: 'Alineando rubros al catálogo', Icon: Layers3 },
  {
    phase: 'suggesting_budget_sheets',
    label: 'Generando planillas de presupuesto sugeridas…',
    Icon: FileSpreadsheet
  },
  { phase: 'saving_draft', label: 'Guardando borrador…', Icon: Save },
  { phase: 'finalizing', label: 'Finalizando', Icon: CheckCircle2 }
]

const phaseIndex = (phase: string | null): number => {
  if (!phase) return -1
  const i = PIPELINE_STEPS.findIndex((s) => s.phase === phase)
  return i >= 0 ? i : -1
}

const unknownPhaseLabel = (phase: string) =>
  phase
    .split(/_/g)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const UPLOADING_DOCUMENT_TITLE = 'Enviando documento…'

type StreamHint = 'processing' | 'still_working' | null

interface DocumentPipelineLoopLoaderProps {
  pipelinePhase?: string | null
  streamHint?: StreamHint
  primaryFileOriginalName?: string | null
  primaryFileSizeBytes?: number | null
}

const DocumentPipelineLoopLoader: React.FC<DocumentPipelineLoopLoaderProps> = ({
  pipelinePhase = null,
  streamHint = null,
  primaryFileOriginalName = null,
  primaryFileSizeBytes = null
}) => {
  const activeIdx = phaseIndex(pipelinePhase)
  const unknown = Boolean(pipelinePhase && activeIdx < 0)
  const unknownLabel = unknown && pipelinePhase ? unknownPhaseLabel(pipelinePhase) : ''
  const total = PIPELINE_STEPS.length
  const stepNum = activeIdx >= 0 ? activeIdx + 1 : null
  const primaryName =
    typeof primaryFileOriginalName === 'string' && primaryFileOriginalName.trim().length > 0
      ? primaryFileOriginalName.trim()
      : null
  const sizeLabel =
    typeof primaryFileSizeBytes === 'number' &&
    primaryFileSizeBytes > 0 &&
    Number.isFinite(primaryFileSizeBytes)
      ? formatDocumentationPipelineFileSize(primaryFileSizeBytes)
      : null
  const indeterminateBar = pipelinePhase === 'extracting_text'
  const progressPct =
    streamHint === 'processing'
      ? 8
      : indeterminateBar
        ? 14
        : activeIdx >= 0
          ? Math.round(((activeIdx + 1) / total) * 100)
          : unknown
            ? 12
            : streamHint === 'still_working'
              ? 10
              : 6

  const stepTitle =
    activeIdx >= 0 ? PIPELINE_STEPS[activeIdx].label : UPLOADING_DOCUMENT_TITLE

  const phaseNamedTitle =
    pipelinePhase === 'extracting_text'
      ? primaryName
        ? `Leyendo ${primaryName}…`
        : 'Leyendo el documento…'
      : pipelinePhase === 'analyzing_document' && primaryName
        ? `Analizando ${primaryName}…`
        : null

  const defaultTitle = unknown
    ? unknownLabel
    : phaseNamedTitle ?? (activeIdx >= 0 ? PIPELINE_STEPS[activeIdx].label : UPLOADING_DOCUMENT_TITLE)

  const mainTitle =
    streamHint === 'processing'
      ? 'Procesando…'
      : streamHint === 'still_working'
        ? defaultTitle === UPLOADING_DOCUMENT_TITLE && pipelinePhase
          ? unknownPhaseLabel(pipelinePhase)
          : defaultTitle
        : defaultTitle

  const eyebrowText =
    primaryName != null
      ? primaryFileSizeBytes != null && primaryFileSizeBytes > 0 && sizeLabel
        ? `Documento · ${sizeLabel}`
        : 'Analizando documento para el borrador'
      : 'Analizando tu PDF para el borrador'

  const showLargePdfHint =
    typeof primaryFileSizeBytes === 'number' && primaryFileSizeBytes >= 3 * 1024 * 1024

  const liveStatus =
    streamHint === 'processing'
      ? 'Procesando.'
      : streamHint === 'still_working'
        ? `${mainTitle}. Sigue en curso.`
        : unknown
          ? `Procesando: ${unknownLabel}`
          : activeIdx >= 0
            ? `${stepTitle}${primaryName ? ` (${primaryName})` : ''}.` +
              (pipelinePhase === 'extracting_text'
                ? ' Esta etapa no muestra porcentaje de avance.'
                : ` Paso ${stepNum} de ${total}.`)
            : 'Enviando documento.'

  const waitingForFirstPhase = activeIdx < 0 && !unknown && streamHint === null

  return (
    <div
      className={styles.wrap}
      aria-busy="true"
      data-edify-doc-loader="stream-ndjson"
    >
      <div className={styles.shimmerRail} aria-hidden>
        <div className={styles.shimmer} />
      </div>
      <div className={styles.progressBarTrack} aria-hidden>
        <div className={styles.progressBarFill} style={{ width: `${progressPct}%` }} />
      </div>
      <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
        {liveStatus}
      </div>
      <div className={styles.header}>
        <AIAgentOrbLoader status="thinking" className={`${orbStyles.thinkingOrb} ${styles.orb}`} />
        <div className={styles.headerText}>
          <span className={styles.eyebrow}>{eyebrowText}</span>
          <span className={styles.title}>{mainTitle}</span>
          {streamHint === 'still_working' ? (
            <span className={styles.progressMetaMuted}>Sigue en curso…</span>
          ) : streamHint === 'processing' ? null : pipelinePhase === 'extracting_text' ? (
            <span className={styles.progressMetaMuted}>
              {sizeLabel ? `Tamaño aproximado: ${sizeLabel}. ` : null}
              Esta etapa no indica porcentaje de avance; los archivos grandes pueden tardar varios
              minutos.
            </span>
          ) : stepNum != null ? (
            <span className={styles.progressMeta}>
              Paso {stepNum} de {total}
              <span className={styles.progressMetaHint}>
                {showLargePdfHint
                  ? 'Los PDF grandes pueden tardar varios minutos al extraer texto.'
                  : 'Los pasos de análisis pueden tardar varios minutos.'}
              </span>
            </span>
          ) : (
            <span className={styles.progressMetaMuted}>
              Conectando con el análisis…
            </span>
          )}
        </div>
      </div>
      <ol className={styles.steps} aria-label="Etapas del análisis">
        {PIPELINE_STEPS.map(({ phase, label, Icon }, i) => {
          const active = activeIdx >= 0 && i === activeIdx
          const done = activeIdx >= 0 && i < activeIdx
          const waitingSlot = waitingForFirstPhase && i === 0
          const stepClass = `${styles.step} ${active ? styles.stepActive : ''} ${done ? styles.stepDone : ''} ${waitingSlot ? styles.stepWaitingServer : ''}`
          return (
            <li
              key={phase}
              className={stepClass}
              aria-current={active ? 'step' : undefined}
            >
              <span className={styles.stepIcon} aria-hidden>
                {done ? <Check size={18} strokeWidth={2.25} /> : <Icon size={18} strokeWidth={1.85} />}
              </span>
              <span className={styles.stepLabel}>{label}</span>
              {active ? <span className={styles.stepNowBadge}>En curso</span> : null}
              {waitingSlot ? (
                <span className={styles.stepWaitingBadge}>En espera…</span>
              ) : null}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default DocumentPipelineLoopLoader
